import { useMutation, useQuery } from '@tanstack/react-query';
import { PersonasAPI } from '@newo/api/gateway';
import type {
    IPersonaActorsParams,
    IActorData,
    TPersonaData,
    TAttributeData
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';
import queryClient from '@/libs/queryClient';

export const personasAPI = new PersonasAPI(axios);

// CREATE Persona
export const useCreatePersonaMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TPersonaData }) =>
            personasAPI.createPersona(data)
    });

// UPDATE Persona
export const useUpdatePersonaMutation = () =>
    useMutation({
        mutationFn: async ({
            personaId,
            data
        }: {
            personaId: string;
            data: TPersonaData;
        }) => personasAPI.updatePersona(personaId, data)
    });

// DELETE Persona
export const useDeletePersonaMutation = () =>
    useMutation({
        mutationFn: async ({ personaId }: { personaId: string }) =>
            personasAPI.deletePersona(personaId)
    });

// GET Personas actors
export const usePersonaActorsQuery = (
    personaId?: string,
    params?: IPersonaActorsParams
) =>
    useQuery({
        queryKey: ['persona/actors', personaId, params],
        queryFn: async () => {
            if (!personaId) {
                throw new Error('Persona ID is required');
            }

            return personasAPI.getPersonaActors(personaId, params);
        },
        enabled: Boolean(personaId)
    });

// CREATE Actor
export const useCreatePersonaActorMutation = () =>
    useMutation({
        mutationFn: async ({
            personaId,
            data
        }: {
            personaId: string;
            data: IActorData;
        }) => personasAPI.createPersonaActor(personaId, data)
    });

// CREATE Persona Attribute
export const useCreatePersonaAttributeMutation = () =>
    useMutation({
        mutationFn: async ({
            personaId,
            data
        }: {
            personaId: string;
            data: TAttributeData;
        }) => personasAPI.createPersonaAttribute(personaId, data)
    });

// UPDATE Persona Attribute
export const useUpdatePersonaAttributeMutation = () =>
    useMutation({
        mutationFn: async ({
            personaId,
            attributeId,
            data
        }: {
            personaId: string;
            attributeId: string;
            data: TAttributeData;
        }) => personasAPI.updatePersonaAttribute(personaId, attributeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personas'] });
        }
    });

// DELETE Persona Attribute
export const useDeletePersonaAttributeMutation = () =>
    useMutation({
        mutationFn: async ({
            personaId,
            attributeId
        }: {
            personaId: string;
            attributeId: string;
        }) => personasAPI.deletePersonaAttribute(personaId, attributeId)
    });
